/**
 * 👤 GUEST CUSTOMER DIALOG
 * Dialog for entering unregistered customer details for admin-created bookings
 */

import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  Typography,
  Alert,
  Box,
} from '@mui/material';
import { PersonAdd, Email, Phone, Badge } from '@mui/icons-material';
import { User } from '@/services/userService';

interface GuestCustomerDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (guestCustomer: User) => void;
}

export const GuestCustomerDialog: React.FC<GuestCustomerDialogProps> = ({
  open,
  onClose,
  onConfirm,
}) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [errors, setErrors] = useState<{ name?: string; email?: string; phone?: string }>({});

  const handleReset = () => {
    setName('');
    setEmail('');
    setPhone('');
    setErrors({});
  };

  const validateForm = (): boolean => {
    const newErrors: { name?: string; email?: string; phone?: string } = {};

    // Name is required
    if (!name.trim()) {
      newErrors.name = 'Customer name is required';
    }

    // At least email or phone is required
    if (!email.trim() && !phone.trim()) {
      newErrors.email = 'Either email or phone number is required';
      newErrors.phone = 'Either email or phone number is required';
    }

    // Validate email format if provided
    if (email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.email = 'Invalid email format';
    }

    // Validate phone format if provided (basic validation)
    if (phone.trim() && !/^[\d\s\-\+\(\)]+$/.test(phone)) {
      newErrors.phone = 'Invalid phone number format';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleConfirm = () => {
    if (!validateForm()) {
      return;
    }

    // Create a guest user object that matches the User interface
    const guestCustomer: User = {
      id: `guest_${Date.now()}`, // Temporary ID for frontend
      name: name.trim(),
      email: email.trim() || undefined,
      phone: phone.trim() || undefined,
      personalInfo: {
        firstName: name.trim().split(' ')[0] || '',
        lastName: name.trim().split(' ').slice(1).join(' ') || '',
        email: email.trim() || undefined,
        phone: phone.trim() || undefined,
      },
      accountInfo: {
        emailVerified: false,
        phoneVerified: false,
      },
      isGuest: true, // Mark as guest customer
      isVerified: false,
    } as User;

    onConfirm(guestCustomer);
    handleReset();
    onClose();
  };

  const handleClose = () => {
    handleReset();
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Stack direction="row" spacing={1} alignItems="center">
          <PersonAdd />
          <Typography variant="h6">Add Guest Customer</Typography>
        </Stack>
      </DialogTitle>

      <DialogContent>
        <Alert severity="info" sx={{ mb: 3 }}>
          Enter details for a customer who is not registered in the system.
          The booking will be created for this guest customer.
        </Alert>

        <Stack spacing={2.5}>
          <TextField
            fullWidth
            label="Customer Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            error={!!errors.name}
            helperText={errors.name}
            required
            placeholder="John Doe"
            InputProps={{
              startAdornment: <Badge sx={{ mr: 1, color: 'action.active' }} />,
            }}
          />

          <TextField
            fullWidth
            label="Email Address"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            error={!!errors.email}
            helperText={errors.email || 'At least email or phone is required'}
            placeholder="customer@example.com"
            InputProps={{
              startAdornment: <Email sx={{ mr: 1, color: 'action.active' }} />,
            }}
          />

          <TextField
            fullWidth
            label="Phone Number"
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            error={!!errors.phone}
            helperText={errors.phone || 'At least email or phone is required'}
            placeholder="+234 800 123 4567"
            InputProps={{
              startAdornment: <Phone sx={{ mr: 1, color: 'action.active' }} />,
            }}
          />

          <Alert severity="warning" sx={{ mt: 1 }}>
            <Typography variant="caption">
              <strong>Note:</strong> This customer will be saved as an unverified guest customer.
              They can complete registration later if they wish to use the mobile app.
            </Typography>
          </Alert>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose}>Cancel</Button>
        <Button variant="contained" onClick={handleConfirm} disabled={!name.trim()}>
          Add Customer
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default GuestCustomerDialog;
